import { useMemo } from 'react'
import { RoleType } from '../../models/Role'
import { useGetInvitesQuery } from '../../store/auth/auth'
import { selectAccountId } from '../../store/auth/authSlice'
import { useAppSelector } from '../../store/hooks'
import { useGetBillingCustomerFreeSeatsQuery } from '../../store/plan/plan'
import { freeSeatsFor } from './requestedGroups'
import { useSeatPurchaseGate } from './seatPurchaseGate'
import { requestedSeatTypeCountFilter } from './teamQueries'
import { useAssistantSeatsOffered } from './useAssistantSeatsOffered'
import { seatShortage } from './useRequestedSeatsSnapshot'

const COUNT_PAGE = { page: 0, size: 1 }

export interface SeatTypeShortage {
  seatType: RoleType
  requested: number
  available: number
  shortage: number
}

interface SeatShortageInput {
  seatType: RoleType
  requested: number
  sellable: boolean
}

export interface RequestedSeatShortage {
  canBuy: boolean
  types: SeatTypeShortage[]
}

export const useRequestedSeatShortage = (): RequestedSeatShortage => {
  const accountId = useAppSelector(selectAccountId)
  const canBuy = useSeatPurchaseGate()
  const { offered: assistantOffered } = useAssistantSeatsOffered()

  const advisorFilter = useMemo(() => requestedSeatTypeCountFilter(RoleType.SEAT), [])
  const assistantFilter = useMemo(() => requestedSeatTypeCountFilter(RoleType.SIDECAR), [])

  const { data: freeSeatsData } = useGetBillingCustomerFreeSeatsQuery({ accountId }, { skip: !canBuy, refetchOnMountOrArgChange: true })
  const advisor = useGetInvitesQuery({ accountId, pageRequest: COUNT_PAGE, filter: advisorFilter }, { skip: !canBuy, refetchOnMountOrArgChange: true })
  const assistant = useGetInvitesQuery(
    { accountId, pageRequest: COUNT_PAGE, filter: assistantFilter },
    { skip: !canBuy || !assistantOffered, refetchOnMountOrArgChange: true },
  )

  return useMemo(() => {
    const seats = freeSeatsData?.data.seats
    const shortageFor = ({ seatType, requested, sellable }: SeatShortageInput): SeatTypeShortage => {
      const available = freeSeatsFor(seatType, seats)
      return {
        seatType,
        requested,
        available: available ?? 0,
        shortage: sellable ? (seatShortage(requested, available) ?? 0) : 0,
      }
    }

    return {
      canBuy,
      types: [
        shortageFor({ seatType: RoleType.SEAT, requested: advisor.data?.page.totalElements ?? 0, sellable: true }),
        shortageFor({ seatType: RoleType.SIDECAR, requested: assistant.data?.page.totalElements ?? 0, sellable: assistantOffered }),
      ],
    }
  }, [advisor.data, assistant.data, assistantOffered, canBuy, freeSeatsData])
}
